import { Session } from '@colplat/core-client';
import {
  accountIconHtml, askConfirm, currencyChipHtml, escapeHtml, formString, markCurrentRow,
  mountCollabField, mountCollabToggle, renderRecordTable,
  type MountedField, type MoneyCurrency, type RecordTableRow,
} from '@colplat/ui-web';
import { apiFetch, failureMessage } from '../api.js';
import { appHref } from '../globals.js';
import { accountIcon, iconUrl } from './webledgerAccountDisplay.js';
import { readCached } from '../offlineReads.js';
import {
  accountPath, ancestorIds, buildAccountTree, collapsibleIds, groupByPurpose, showsOwnLine, visibleRows,
  type AccountNode,
} from './webledgerAccountTree.js';
import type { AccountDto } from './webledgerOfflineState.js';
import { accountHistories, accountsView, type AccountHistories, type Ledger } from '@colplat/app-webledger/domain';
import { datasetLedger } from './webledgerLedger.js';
import { parseIdList, PREF, serializeIdList } from './webledgerPrefs.js';
import { restoreScroll, trackScroll, type ScrollTracker } from './webledgerScroll.js';
import { SHOW_PURPOSES } from './webledgerFeatures.js';
import {
  balancesOn, buildAccountChart, changeDays, chartTotalHtml, legendHtml, walletKeys, wireAccountCharts,
  wireAccountHover, type AccountChart, type HoverControl,
} from './webledgerAccountsChart.js';
import { dayAt, scrollToDay, type SparkScroll } from './webledgerSparkline.js';
import { wireInspectGestures } from './webledgerInspect.js';
import { convertTotals, nonZeroCurrencies, type FxRates } from './webledgerReportsData.js';
import {
  bootstrapWebledgerClient, clearPageNotice, pageFailure, showPageNotice, webledgerShellHtml,
  wireBackButton,
} from '../webledgerClient.js';
import { wireHeadReveal } from './webledgerHeadReveal.js';

/**
 * The chart-of-accounts page — webledger's landing page, matching legacy's
 * own `index.php`. Also owns purpose *tagging* (a checkbox per purpose in
 * the account modal), since that's account-centric even though purposes
 * themselves are created on the settings page.
 */

interface BootstrapModalLike { show(): void; hide(): void }
function bsModal(el: Element): BootstrapModalLike | undefined {
  return (window as unknown as { bootstrap?: { Modal: { getOrCreateInstance(e: Element): BootstrapModalLike } } })
    .bootstrap?.Modal.getOrCreateInstance(el);
}

interface PurposeDto { id: string; name: string; sortOrder: number; iconPath?: string }

/** Everything the tree needs beyond the accounts themselves — one bag, so the renderers stay pure-ish. */
interface AccountsView {
  mode: 'location' | 'purpose';
  collapsed: Set<string>;
  /** `'by'` for a chip per currency, `'folder'` for each account's own, or a code. */
  totalMode: string;
  rates: Record<string, number>;
  currencyByCode: Map<string, MoneyCurrency>;
  purposes: PurposeDto[];
  /**
   * The row carrying the "you are here" marker, so a re-render (a collapse,
   * a refresh) puts it back rather than losing the reader's place.
   */
  currentId: string | null;
  /** Attaches the drag-to-reorder handlers after each render. */
  wireDrag: (container: HTMLElement) => void;
  /**
   * The balance-over-time column, when it is switched on — and only then
   * computed, because it is a pass over every leg for every account and
   * the page repaints on every sync.
   */
  chart: AccountChart | undefined;
}
interface AccountPurposeDto { id: string; accountId: string; purposeId: string }

export async function mountWebledgerAccounts(root: HTMLElement): Promise<void> {
  root.innerHTML = shellHtml();
  wireBackButton(root);
  // The page's bar stays at the top as the small header; a tap on it brings
  // the whole header back (webledgerHeadReveal).
  wireHeadReveal(root.querySelector<HTMLElement>('.wl-acct-bar')!);

  const client = await bootstrapWebledgerClient(root);
  if (!client) return;
  const { token, session, prefs } = client;
  const fail = pageFailure(root, client.notify);

  const { dataset, docCache } = client;
  /*
   * The page's own numbers come from the replica, not from a request
   * (ADR-0025) — the same move the wallet pages made, and the reason this one
   * is now live.
   *
   * `accountsView` is the function `services/api` runs to answer
   * `/apps/webledger/accounts`, so the balances are computed here from the
   * rows this device already holds. Two things follow, and both were missing
   * before. The page works with no connection, like every other page. And it
   * recomputes whenever the rows move — which is what makes somebody else's
   * save in another window land on these totals instead of leaving them
   * quietly wrong until a reload.
   *
   * Rates are the one thing left on a cached read: an exchange rate is not a
   * row of this ledger and nothing on this device can derive it.
   */
  const ledger = (): Ledger => datasetLedger(dataset);
  const fxRead = await readCached<FxRates>(docCache, 'fx', '/apps/webledger/fx', token);

  let accounts: AccountDto[] = [];
  let purposes: PurposeDto[] = [];
  let accountPurposes: AccountPurposeDto[] = [];
  let currencies: Array<MoneyCurrency & { id: string; name: string }> = [];
  const fx = fxRead.ok ? fxRead.data : undefined;

  /** Re-read everything this page shows from the replica. Synchronous — nothing is fetched. */
  function readReplica(): void {
    const rows = ledger();
    accounts = accountsView(rows);
    purposes = [...rows.purposes()]
      .map((p) => ({ id: p.id, name: p.name, sortOrder: p.sortOrder ?? 0, iconPath: p.iconPath ?? '' }))
      .sort((a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name));
    accountPurposes = rows.accountPurposes()
      .map((ap) => ({ id: ap.id, accountId: ap.accountId, purposeId: ap.purposeId }));
    currencies = [...rows.currencies()]
      .map((c) => ({
        id: c.id, code: c.code, name: c.name,
        symbol: c.symbol ?? '', fraction: c.fraction ?? 2,
      }))
      .sort((a, b) => a.code.localeCompare(b.code));
  }
  readReplica();

  /*
   * `roles` is the same list `requireWebledgerMember` checks on the server —
   * membership of webledger, nothing more — so an empty one is not a guess
   * about why a read failed, it is the answer the server would have given.
   * The other reason there may be nothing to draw, a device that has never
   * synced, is handled in `paint` instead: it is not an error and it corrects
   * itself.
   */
  if (client.roles.length === 0) {
    root.querySelector('#wlBody')!.innerHTML = '<p class="text-secondary">You do not have access to this app.</p>';
    return;
  }

  /**
   * How the tree is being looked at. All three survive a reload, and now
   * follow the person rather than the browser (webledgerPrefs.ts) — which
   * matters most for `collapsed`: re-collapsing a 59-account chart by hand on
   * every machine is the kind of small tax that makes a tool feel hostile.
   */
  // Listening from here, recording only once the page has finished arriving.
  const scrollTracker: ScrollTracker = trackScroll(prefs, PREF.scrollAccounts);
  const view: AccountsView = {
    mode: prefs.get(PREF.accountsView) === 'purpose' ? 'purpose' : 'location',
    collapsed: new Set(parseIdList(prefs.get(PREF.accountsCollapsed))),
    totalMode: prefs.get(PREF.accountsTotalMode) ?? 'by',
    rates: fx?.rates ?? {},
    currencyByCode: new Map(currencies.map((c) => [c.code, c] as const)),
    purposes: [],
    // The account you were last in is where you are, before you touch
    // anything — so the marker is already on it when the page first paints.
    currentId: prefs.get(PREF.lastAccount) ?? null,
    wireDrag: (container) => wireAccountDrag(container, token, repaint, fail),
    chart: undefined,
  };
  /** Off unless asked for: it is a wide column on a page people mostly open to read totals. */
  let chartOn = prefs.get(PREF.accountsChart) === '1';
  /** Shared by every chart in the column, and kept across re-renders so a sync does not scroll you home. */
  const chartScroll: SparkScroll = { position: -1 };
  /**
   * The date being inspected (see `inspect`), and the chart kept until the
   * replica moves — an arrow press re-renders and need not recount every leg.
   * Up here with the rest of the page's state: the first paint reads both,
   * and runs before anything declared further down exists.
   */
  let inspectDay: string | undefined;
  /** The breakdown hover, so a step can make it answer again. */
  let hover: HoverControl | undefined;
  let chartCache: { revision: number; chart: AccountChart | undefined } | undefined;

  /*
   * Repaint whenever the replica moves — the whole of this page's liveness.
   *
   * It listened to `recordEvent` for `account`, `purpose` and
   * `accountPurpose` before, which was narrower than it looked in two ways.
   * A transaction is none of those, so every balance on the page sat still
   * while somebody else moved money. And a *field* edit — renaming an
   * account, which is most of what happens here — is announced as
   * `committed` to whoever has that record open and reached a second window
   * not at all.
   *
   * Both are now the same event as far as this page is concerned: the rows
   * changed, so read them again. Which also means the page no longer cares
   * where the change came from — another window, another person, this
   * device's own outbox replaying after a flight.
   */
  /*
   * `revision` rather than the bare notification: a delta usually carries no
   * rows and notifies anyway, and each pass here rebuilds a fifty-nine row
   * tree under whoever is reading it.
   */
  let drawnAt = -1;
  dataset.subscribe(() => {
    if (dataset.revision() === drawnAt) return;
    drawnAt = dataset.revision();
    paint();
  });

  wireNewAccountForm(root, token, repaint);
  // Merging is destructive and admin-only, so the control is not shown to
  // someone who would only be refused by the server.
  if (client.roles.includes('webledger-admin')) {
    root.querySelector<HTMLElement>('#acctMergeBtn')!.hidden = false;
    wireMergeForm(root, token, () => accounts, repaint, client.notify);
  }
  wireToolbar();
  wireRowMarker();
  wireBreakdownHover();
  wireInspect();
  paint();
  // Position first, marker second. `flashLastAccount` only moves the page
  // when the row it wants is *not* where the restored position already put
  // it — see `markCurrentRow`'s `scroll`.
  await restoreScroll(prefs, PREF.scrollAccounts);
  scrollTracker.start();
  flashLastAccount();

  /**
   * Point at the account you were last in — legacy's `flashLastAccount`.
   *
   * Coming back from a register to a 59-row tree and having to find your
   * place again is a small tax paid every single time. Any collapsed
   * ancestors are opened first, otherwise the row is not there to point at.
   *
   * The flash is the loud half of the marker; the quiet half stays on the
   * row afterwards. Both matter here, because "returning" is not one moment:
   * the page can be back for several seconds before the reader's eyes are.
   */
  function flashLastAccount(): void {
    const id = prefs.get(PREF.lastAccount);
    if (!id || !accounts.some((a) => a.id === id)) return;

    view.currentId = id;
    const hidden = ancestorIds(accounts, id).filter((ancestor) => view.collapsed.has(ancestor));
    if (hidden.length > 0) {
      for (const ancestor of hidden) view.collapsed.delete(ancestor);
      persistCollapsed();
      renderAccounts();          // the row does not exist until its branch is open
    }
    markCurrentRow(root, id, { flash: true, scroll: 'if-needed' });
  }

  /**
   * Clicking a row moves the marker to it — legacy's global row-pick
   * handler, and what makes a lasting tint mean something rather than appear
   * from nowhere: it is where *you* last were, not where the app decided.
   *
   * Delegated, because the table is rebuilt on every render.
   */
  function wireRowMarker(): void {
    root.querySelector('#accountsTable')?.addEventListener('click', (event) => {
      const target = event.target as HTMLElement;
      // A caret is a disclosure, not a destination, and a currency chip is
      // its own gesture. Neither is a statement about where the reader is.
      if (target.closest('[data-toggle-account], .cf-cur-chip')) return;
      const row = target.closest<HTMLElement>('tr[data-row]');
      if (!row) return;
      view.currentId = row.dataset['row'] ?? null;
      markCurrentRow(root, view.currentId);
    });

    // A page restored from the back/forward cache never re-runs any of the
    // above: the DOM comes back exactly as it was left, marker included, but
    // nothing announces the return. Re-flash the row that is already marked
    // — which, because a click moves the marker, is the account just
    // visited.
    window.addEventListener('pageshow', (event) => {
      if (event.persisted) markCurrentRow(root, view.currentId, { flash: true, scroll: 'if-needed' });
    });
  }

  /**
   * Hovering a chart, or any total's chip, shows what the account holds by
   * wallet and currency — on that day, or now.
   *
   * The histories come from the chart when it is on. When it is off they are
   * worked out the first time somebody hovers, and kept until the replica
   * next moves: a pass over every leg is not worth making on every render
   * for a tooltip nobody may ask for.
   */
  // ------------------------------------------------- inspecting one date

  /**
   * The date the page is showing, when somebody has clicked a chart.
   *
   * A click on any chart picks its day: a line is drawn through every chart
   * at it, the Total column says what each account held that evening, and
   * every breakdown answers for it. The arrow keys — or a swipe on a chart,
   * on a touch screen — step to the previous or next day on which any total
   * changed. A click or tap anywhere that is not a chart puts the page back
   * on today.
   */
  function chartFor(): AccountChart | undefined {
    if (chartCache?.revision !== dataset.revision()) {
      chartCache = { revision: dataset.revision(), chart: buildAccountChart(ledger()) };
    }
    return chartCache.chart;
  }

  function inspect(day: string | undefined): void {
    if (day === inspectDay) return;
    inspectDay = day;
    // Leaving the inspected day lets go of a pinned tooltip too: it was a
    // window onto that day.
    if (!day) hover?.unpin();
    renderAccounts();
    // The figures moved under a pointer that did not.
    hover?.refresh();
  }

  /** The previous or next day any total changed, from the one inspected. */
  function step(direction: 1 | -1): void {
    const chart = view.chart;
    if (!chart || !inspectDay) return;
    const days = changeDays(chart.histories);
    const next = direction > 0 ? days.find((d) => d > inspectDay!) : [...days].reverse().find((d) => d < inspectDay!);
    if (next) inspect(next);
  }

  /** Says the page is not showing today, and how to get back — the line alone would be a colour saying it. */
  function paintInspectNote(): void {
    const note = root.querySelector<HTMLElement>('#acctInspect')!;
    root.querySelector('#accountsTable')!.classList.toggle('wl-inspecting', Boolean(inspectDay));
    note.hidden = !inspectDay;
    note.innerHTML = inspectDay
      ? `Totals as they stood on <strong>${escapeHtml(inspectDay)}</strong>. `
        + '<span class="text-secondary">← → (or swipe a chart) step to the previous or next change; '
        + 'click outside the charts to return to today.</span>'
      : '';
  }

  function wireInspect(): void {
    wireInspectGestures(root.querySelector<HTMLElement>('#accountsTable')!, {
      active: () => Boolean(inspectDay),
      pick: (box, clientX) => {
        if (!view.chart) return;
        const rect = box.getBoundingClientRect();
        inspect(dayAt(view.chart.span, clientX - rect.left + box.scrollLeft));
      },
      step,
      exit: () => inspect(undefined),
      pin: () => { hover?.togglePin(); },
      chartSelector: '.wl-spark[data-acct]',
    });
  }

  function wireBreakdownHover(): void {
    let cached: { revision: number; histories: AccountHistories } | undefined;
    hover = wireAccountHover(root.querySelector<HTMLElement>('#accountsTable')!, {
      chart: () => view.chart,
      histories: () => {
        if (view.chart) return view.chart.histories;
        if (cached?.revision !== dataset.revision()) {
          cached = { revision: dataset.revision(), histories: accountHistories(ledger()) };
        }
        return cached.histories;
      },
      keys: () => view.chart ?? walletKeys(ledger()),
      nameOf: (id) => accounts.find((a) => a.id === id)?.name ?? '',
      // "By purpose" lists each account's own balance; the tree's Total is
      // the subtree, and its "own" line is the account alone.
      cutOf: (chip) => (view.mode === 'purpose' || chip.closest('.wl-acct-own') ? 'own' : 'subtree'),
      money: (code) => view.currencyByCode.get(code),
      inspectDay: () => inspectDay,
    });
  }

  function wireToolbar(): void {
    const modeSelect = root.querySelector<HTMLSelectElement>('#acctViewMode')!;
    const totalSelect = root.querySelector<HTMLSelectElement>('#acctTotalMode')!;
    modeSelect.value = view.mode;
    // Only the currencies flagged for totals, plus the two structural modes —
    // a picker of 166 codes is a worse answer than the three the book is
    // actually kept in.
    totalSelect.innerHTML = '<option value="by">By currency</option>'
      + '<option value="folder">In each account\'s own currency</option>'
      + (fx?.totalsCurrencies ?? []).map((c) => `<option value="${escapeHtml(c.code)}">In ${escapeHtml(c.code)}</option>`).join('');
    totalSelect.value = [...totalSelect.options].some((o) => o.value === view.totalMode) ? view.totalMode : 'by';
    view.totalMode = totalSelect.value;

    modeSelect.addEventListener('change', () => {
      view.mode = modeSelect.value === 'purpose' ? 'purpose' : 'location';
      prefs.set(PREF.accountsView, view.mode);
      renderAccounts();
    });
    totalSelect.addEventListener('change', () => {
      view.totalMode = totalSelect.value;
      prefs.set(PREF.accountsTotalMode, view.totalMode);
      renderAccounts();
    });
    const chartToggle = root.querySelector<HTMLInputElement>('#acctChartToggle')!;
    chartToggle.checked = chartOn;
    chartToggle.addEventListener('change', () => {
      chartOn = chartToggle.checked;
      prefs.set(PREF.accountsChart, chartOn ? '1' : '');
      renderAccounts();
    });
    root.querySelector('#acctExpandAll')?.addEventListener('click', () => {
      view.collapsed.clear();
      persistCollapsed();
      renderAccounts();
    });
    root.querySelector('#acctCollapseAll')?.addEventListener('click', () => {
      for (const id of collapsibleIds(buildAccountTree(accounts))) view.collapsed.add(id);
      persistCollapsed();
      renderAccounts();
    });
    // Delegated: the table is rebuilt on every render, so a handler bound to
    // a caret would survive exactly one toggle.
    root.querySelector('#accountsTable')?.addEventListener('click', (event) => {
      const caret = (event.target as HTMLElement).closest<HTMLElement>('[data-toggle-account]');
      if (!caret) return;
      const id = caret.dataset['toggleAccount']!;
      if (view.collapsed.has(id)) view.collapsed.delete(id);
      else view.collapsed.add(id);
      persistCollapsed();
      renderAccounts();
    });
  }

  function persistCollapsed(): void {
    prefs.set(PREF.accountsCollapsed, serializeIdList(view.collapsed));
  }

  /**
   * Re-read the rows and draw the page. The only way anything gets on screen.
   *
   * Called on every replica change, and by this page's own actions once their
   * write has been accepted. A write of ours reaches the replica the way
   * anybody else's does — the server announces it, `ReplicaSync` fetches the
   * delta, the subscription fires — and that is a round trip the person who
   * pressed the button should not have to watch, so they paint from what is
   * already held and let the delta confirm it. Reading the same rows twice
   * costs a render and cannot disagree with itself.
   */
  function paint(): void {
    readReplica();
    // Any failure still on screen was about rows that have just been re-read,
    // so it is now describing something that is no longer true.
    clearPageNotice(root);
    /*
     * Nothing held, and nothing ever synced here.
     *
     * Not an error, and deliberately a notice rather than a replacement for
     * the page: the chart of accounts below it is empty because this device
     * has not been told what is in it yet, and the moment a snapshot lands
     * this runs again and the notice goes. Blanking the body instead would
     * remove the very table that repaint draws into.
     */
    if (accounts.length === 0 && dataset.isEmpty()) {
      showPageNotice(root, 'This page has not been loaded on this device yet, and there is no connection. '
        + 'Open it once while online and it will keep working offline afterwards.');
    }
    renderAccounts();
    // New figures under a tooltip that may be pinned — a save in another
    // window arrives here by sync, and the pinned day should show it, not
    // wait for a step through dates to ask again.
    hover?.refresh();
  }
  /** The same thing in the shape the modal and the toolbar hand their callbacks. */
  function repaint(): Promise<void> {
    paint();
    return Promise.resolve();
  }

  function renderAccounts(): void {
    view.purposes = purposes;
    // Rebuilt each time rather than captured once: a currency added on the
    // settings page now arrives here by sync like anything else, and a map
    // built at mount would format its chips as an unknown code for as long
    // as the page stayed open.
    view.currencyByCode = new Map(currencies.map((c) => [c.code, c] as const));
    view.chart = chartOn ? chartFor() : undefined;
    if (!view.chart) inspectDay = undefined;
    if (view.chart) view.chart = { ...view.chart, ...(inspectDay ? { markDay: inspectDay } : {}) };
    const legend = root.querySelector<HTMLElement>('#acctChartLegend')!;
    legend.hidden = !view.chart;
    legend.innerHTML = view.chart ? legendHtml(view.chart) : '';
    paintInspectNote();
    root.querySelector('#accountsTable')!.classList.toggle('wl-acct--charts', Boolean(view.chart));
    // While a date is inspected, every account shows what it held that
    // evening; the tree adds folders up from those, exactly as it does today.
    const shown = inspectDay && view.chart
      ? accounts.map((a) => ({ ...a, balances: balancesOn(view.chart!.histories.own.get(a.id), inspectDay!) }))
      : accounts;
    // Where the table was scrolled sideways, kept across the redraw: on a
    // narrow screen the charts are off to the right, and stepping through
    // dates redraws on every press — which put the table back at its left
    // edge and the charts out of sight.
    const sideways = root.querySelector<HTMLElement>('#accountsTable .cf-table-scroll')?.scrollLeft ?? 0;
    renderAccountsTable(root, session, shown, view, (account) => {
      // Getters, not snapshots: a refresh below reassigns the outer
      // `purposes`/`accountPurposes` bindings, which a plain value argument
      // captured at modal-open time would never see — the checkbox list
      // would silently keep showing pre-toggle state on every render.
      openAccountModal(
        root, session, account, () => purposes, () => accountPurposes, token,
        repaint, repaint, client!.notify,
      );
    });
    populateParentAccountOptions(root, accounts);
    const scroller = root.querySelector<HTMLElement>('#accountsTable .cf-table-scroll');
    if (scroller) scroller.scrollLeft = sideways;
    if (view.chart) {
      if (inspectDay) {
        scrollToDay(root.querySelector<HTMLElement>('#accountsTable .wl-spark[data-acct]'), view.chart.span, inspectDay, chartScroll);
      }
      wireAccountCharts(root.querySelector<HTMLElement>('#accountsTable')!, chartScroll);
    }
    // Re-applied after every render, not just the first: collapsing a branch
    // or a live refresh rebuilds the table, and a marker that vanished on
    // the next `recordEvent` would be worse than not having one.
    markCurrentRow(root, view.currentId);
    // Collapsing a branch makes the page shorter, which clamps the window and
    // reads as a scroll the reader never made.
    scrollTracker.settling();
  }
}

function shellHtml(): string {
  return webledgerShellHtml('accounts', `
    <div class="cf-card cf-card--bleed p-3">
      <div class="wl-acct-bar mb-2">
        <div class="cf-h">Accounts</div>
        <div class="btn-group btn-group-sm" role="group" aria-label="Expand or collapse the whole tree">
          <button class="btn btn-outline-secondary" type="button" id="acctExpandAll">Expand all</button>
          <button class="btn btn-outline-secondary" type="button" id="acctCollapseAll">Collapse all</button>
        </div>
        <select class="form-select form-select-sm wl-acct-bar__sel" id="acctViewMode" aria-label="Group accounts by">
          <option value="location">By location</option>
          <option value="purpose">By purpose</option>
        </select>
        <select class="form-select form-select-sm wl-acct-bar__sel" id="acctTotalMode" aria-label="Show totals by currency or converted"></select>
        <div class="form-check form-switch wl-acct-bar__switch mb-0">
          <input class="form-check-input" type="checkbox" role="switch" id="acctChartToggle">
          <label class="form-check-label" for="acctChartToggle">Balance over time</label>
        </div>
        <button class="btn btn-sm btn-outline-secondary ms-auto" type="button" id="acctMergeBtn" hidden
          data-bs-toggle="modal" data-bs-target="#mergeAccountsModal">Merge accounts</button>
        <button class="btn btn-sm btn-outline-secondary" type="button" data-bs-toggle="modal" data-bs-target="#newAccountModal">New account</button>
      </div>
      <div id="acctChartLegend" class="wl-acct-legend mb-2" hidden></div>
      <div id="acctInspect" class="wl-acct-inspect mb-2" role="status" aria-live="polite" hidden></div>
      <div id="accountsTable"></div>
    </div>`) +
    accountModalHtml() + newAccountModalHtml() + mergeAccountsModalHtml() +
    `<div class="cf-sr" aria-live="polite" aria-atomic="true"></div>`;
}

export function accountModalHtml(): string {
  return `
    <div class="modal fade" id="accountModal" tabindex="-1" aria-hidden="true">
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content">
          <div class="modal-header py-2">
            <div class="fw-semibold" id="accountModalTitle">Account</div>
            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
          </div>
          <div class="modal-body pt-4" id="accountModalFields"></div>
          <div class="modal-footer py-2 d-block">
            ${SHOW_PURPOSES ? '<div class="cf-h mb-2">Purposes</div><div id="accountModalPurposes"></div>' : ''}
            <div class="d-flex align-items-center gap-2 mt-3 pt-2" style="border-top:1px solid #dde3ea">
              <span class="cf-h">Icon</span>
              <span id="accountIconPreview"></span>
              <label class="btn btn-sm btn-outline-secondary mb-0">Choose…
                <input type="file" id="accountIconFile" accept=".svg,.png,.jpg,.jpeg,image/svg+xml,image/png,image/jpeg" hidden>
              </label>
              <button class="btn btn-sm btn-outline-secondary" type="button" id="accountIconClear">Remove</button>
              <span class="small text-secondary" id="accountIconNote"></span>
            </div>
            <div class="d-flex align-items-center gap-2 mt-3 pt-2" style="border-top:1px solid #dde3ea">
              <button class="btn btn-sm btn-outline-danger" type="button" id="accountDelete">Delete account</button>
              <span class="small text-secondary" id="accountDeleteNote"></span>
            </div>
          </div>
        </div>
      </div>
    </div>`;
}

function newAccountModalHtml(): string {
  return `
    <div class="modal fade" id="newAccountModal" tabindex="-1" aria-hidden="true">
      <div class="modal-dialog">
        <div class="modal-content">
          <div class="modal-header py-2">
            <div class="fw-semibold">New account</div>
            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
          </div>
          <form id="newAccountForm">
            <div class="modal-body">
              <div class="mb-2"><label class="form-label">Name</label><input class="form-control" name="name" required></div>
              <div class="mb-2"><label class="form-label">Type</label>
                <select class="form-select" name="type" required>
                  <option value="ASSET">Asset</option><option value="LIABILITY">Liability</option>
                  <option value="EQUITY">Equity</option><option value="INCOME">Income</option>
                  <option value="EXPENSE">Expense</option>
                </select></div>
              <div class="mb-2"><label class="form-label">Currency code</label>
                <input class="form-control" name="currencyCode" maxlength="3" placeholder="EUR" required></div>
              <div class="mb-2"><label class="form-label">Parent account</label>
                <select class="form-select" name="parentId" id="newAccountParent"><option value="">(top level)</option></select></div>
              <div class="form-check"><input class="form-check-input" type="checkbox" name="isPlaceholder" id="newAccountPlaceholder">
                <label class="form-check-label" for="newAccountPlaceholder">Placeholder (folder — cannot hold transactions)</label></div>
            </div>
            <div class="modal-footer py-2"><button class="btn btn-primary btn-sm" type="submit">Create</button></div>
          </form>
        </div>
      </div>
    </div>`;
}

/** Re-populated every time the account list changes — the static shell HTML can't know this at page-load time. */
function populateParentAccountOptions(root: HTMLElement, accounts: AccountDto[]): void {
  const select = root.querySelector<HTMLSelectElement>('#newAccountParent');
  if (!select) return;
  const previous = select.value;
  // Fully expanded regardless of what the tree is showing: a parent picker
  // that hid collapsed branches would make some accounts unpickable.
  const options = visibleRows(buildAccountTree(accounts), new Set())
    .map((node) => `<option value="${node.account.id}">`
      + `${'\u00a0\u00a0'.repeat(node.depth)}${escapeHtml(node.account.name)}</option>`)
    .join('');
  select.innerHTML = `<option value="">(top level)</option>${options}`;
  if ([...select.options].some((o) => o.value === previous)) select.value = previous;
}

/**
 * One row per account, indented, with a caret where there are children —
 * legacy's `renderNode`.
 *
 * Rendered through `renderRecordTable` rather than hand-rolled so the rows
 * keep their presence gutter: accounts are field-edited collaboratively, and
 * a tree that forgot to say who is in a row would be a step backwards from
 * the flat list it replaces.
 */
function renderAccountsTable(
  root: HTMLElement, session: Session, accounts: AccountDto[], view: AccountsView,
  onOpen: (a: AccountDto) => void,
): void {
  const container = root.querySelector<HTMLElement>('#accountsTable')!;
  const money = (code: string): MoneyCurrency | undefined => view.currencyByCode.get(code);

  if (view.mode === 'purpose') {
    renderPurposeView(container, accounts, view, money, onOpen);
    wireOpenButtons(container, accounts, onOpen);
    return;
  }

  const tree = buildAccountTree(accounts);
  const rows: RecordTableRow[] = visibleRows(tree, view.collapsed).map((node) => {
    const a = node.account;
    const caret = node.children.length > 0
      ? `<button type="button" class="wl-caret" data-toggle-account="${a.id}" `
        + `aria-expanded="${!view.collapsed.has(a.id)}" aria-label="${view.collapsed.has(a.id) ? 'Expand' : 'Collapse'} ${escapeHtml(a.name)}">`
        // The chevron turns with aria-expanded (styles.css): down open, right shut.
        + '<span class="cf-exp" aria-hidden="true"></span></button>'
      : '<span class="wl-caret wl-caret--empty" aria-hidden="true"></span>';

    // A folder is a grouping node and cannot hold transactions, so its name
    // is not a link to a register — it links to the folder's own report,
    // which is what "all transactions in here" means.
    // A folder holds no transactions of its own, so its name opens the
    // folder's report — "everything in here". A posting account opens its own
    // register, which is the page written from that account's point of view.
    const name = a.isPlaceholder
      ? `<a class="fw-semibold" href="${escapeHtml(appHref(`/webledger/reports?folder=${a.id}`))}" `
        + `title="All transactions in this folder">${escapeHtml(a.name)}</a>`
      : `<a href="${escapeHtml(appHref(`/webledger/accounts/${a.id}/register`))}">${escapeHtml(a.name)}</a>`;

    const own = showsOwnLine(node)
      ? `<div class="wl-acct-own"><span class="wl-acct-own__lbl">own</span>`
        + `${chipsFor(node.own, money)}</div>`
      : '';

    return {
      id: a.id,
      // `draggable` plus the two attributes the drop rules test: a drag is
      // valid only between siblings of the same type, so a gesture can never
      // reparent an account or turn an expense into an asset.
      attrs: ` draggable="true" data-parent="${escapeHtml(a.parentId ?? '')}" data-type="${escapeHtml(a.type)}"`,
      html:
        `<td class="cf-title"><div class="wl-acct-name" style="padding-left:${node.depth * 1.15}rem">`
        + `${caret}${accountIcon(a)}<span>${name}</span>`
        + `<button type="button" class="wl-acct-edit" data-open-account="${a.id}" aria-label="Edit ${escapeHtml(a.name)}">Edit</button>`
        + `</div>${own}</td>`
        + `<td><span class="badge text-bg-light">${escapeHtml(a.type)}</span></td>`
        + `<td class="text-secondary">${escapeHtml(a.currencyCode)}</td>`
        + `<td class="wl-num">${view.chart
          // The subtree, because that is what the Total adds up: a folder's
          // chart is everything filed under it.
          ? chartTotalHtml(node.subtree, view.chart.histories.subtree.get(a.id), view.chart, a.id, 'subtree',
            money, view.totalMode === 'by' ? undefined : totalCell(node, view, money))
          : totalCell(node, view, money)}</td>`,
    };
  });

  renderRecordTable(container, {
    session, entity: 'account', headers: ['Name', 'Type', 'Currency', 'Total'], rows,
  });
  wireOpenButtons(container, accounts, onOpen);
  view.wireDrag(container);
}

function wireOpenButtons(container: HTMLElement, accounts: AccountDto[], onOpen: (a: AccountDto) => void): void {
  container.querySelectorAll<HTMLButtonElement>('[data-open-account]').forEach((btn) => {
    btn.addEventListener('click', () => {
      const account = accounts.find((a) => a.id === btn.dataset['openAccount']);
      if (account) onOpen(account);
    });
  });
}

function chipsFor(balances: Record<string, string>, money: (c: string) => MoneyCurrency | undefined): string {
  const codes = nonZeroCurrencies(balances);
  if (codes.length === 0) return '<span class="text-secondary">–</span>';
  return `<span class="cf-cur-chips">${codes.map((c) => {
    const currency = money(c);
    return currencyChipHtml(balances[c]!, c, currency ? { currency } : {});
  }).join('')}</span>`;
}

/**
 * The total cell, honouring the chosen mode — legacy's `renderTotalCell`.
 *
 * `folder` converts each account's subtree into *that account's own*
 * currency, which is the mode that makes a multi-currency folder readable:
 * every row answers "what is this worth in the currency this account is kept
 * in". A missing rate is called out rather than silently dropped.
 */
function totalCell(
  node: AccountNode, view: AccountsView, money: (c: string) => MoneyCurrency | undefined,
): string {
  if (view.totalMode === 'by') return chipsFor(node.subtree, money);
  const target = view.totalMode === 'folder' ? node.account.currencyCode : view.totalMode;
  const { amount, missing } = convertTotals(node.subtree, target, view.rates);
  if (nonZeroCurrencies(node.subtree).length === 0) return '<span class="text-secondary">–</span>';
  const currency = money(target);
  const warning = missing.length > 0
    ? ` <span class="wl-fx-warn" title="No rate for ${escapeHtml(missing.join(', '))} — not included">⚠</span>`
    : '';
  return currencyChipHtml(String(amount), target, currency ? { currency } : {}) + warning;
}

/**
 * "By purpose": the same accounts grouped by tag instead of by position.
 *
 * A purpose is a set, not a hierarchy, so members show their full path rather
 * than an indent, and the group total sums their *own* balances — see
 * `groupByPurpose` for why subtrees would double-count.
 */
export function renderPurposeView(
  container: HTMLElement, accounts: AccountDto[], view: AccountsView,
  money: (c: string) => MoneyCurrency | undefined, onOpen: (a: AccountDto) => void,
): void {
  const groups = groupByPurpose(accounts, view.purposes);
  if (groups.length === 0) {
    container.innerHTML = '<p class="text-secondary mb-0">No purposes are in use yet. '
      + 'Tag accounts with a purpose in the account editor to use this view.</p>';
    return;
  }
  container.innerHTML = `<div class="cf-table-scroll"><table class="table cf-table align-middle wl-acct-purpose">
    <thead><tr><th>Account</th><th>Type</th><th>Currency</th><th class="wl-num">Total</th></tr></thead>
    <tbody>${groups.map((group) => {
    const header = `<tr class="wl-purpose-row">
        <td colspan="3"><span class="fw-semibold">${escapeHtml(group.name)}</span>
          <span class="text-secondary small"> · ${group.accounts.length} account${group.accounts.length === 1 ? '' : 's'}</span></td>
        <td class="wl-num">${chipsFor(group.totals, money)}</td></tr>`;
    const members = group.accounts.map((a) => `<tr data-row="${a.id}">
        <td class="cf-title"><div class="wl-acct-name" style="padding-left:1.15rem">
          ${accountIcon(a)}<span class="text-secondary">${escapeHtml(accountPath(accounts, a.id))}</span>
          <button type="button" class="wl-acct-edit" data-open-account="${a.id}" aria-label="Edit ${escapeHtml(a.name)}">Edit</button>
        </div></td>
        <td><span class="badge text-bg-light">${escapeHtml(a.type)}</span></td>
        <td class="text-secondary">${escapeHtml(a.currencyCode)}</td>
        <td class="wl-num">${view.chart
          // Own legs only, matching the figure — see `groupByPurpose`.
          ? chartTotalHtml(a.balances ?? {}, view.chart.histories.own.get(a.id), view.chart, a.id, 'own', money)
          : chipsFor(a.balances ?? {}, money)}</td></tr>`).join('');
    return header + members;
  }).join('')}</tbody></table></div>`;

  /*
   * The Edit buttons this just wrote are inert until they are wired, and
   * they were: the parameter was `_onOpen`, which says "deliberately
   * unused" and silenced the one warning that would have pointed here.
   *
   * The tree view wires its own in `renderAccountsTable`. Nothing shared
   * does it, so a second view that draws the same button has to ask as
   * well — which is what this is, and why the button looked alive on hover
   * and did nothing on click.
   */
  wireOpenButtons(container, accounts, onOpen);
}

function openAccountModal(
  root: HTMLElement, session: Session, account: AccountDto,
  getPurposes: () => PurposeDto[], getAccountPurposes: () => AccountPurposeDto[], token: string,
  onTagsChanged: () => Promise<void>, onDeleted: () => Promise<void>, notify: (m: string) => void,
): void {
  const modalEl = root.querySelector<HTMLElement>('#accountModal')!;
  const titleEl = root.querySelector<HTMLElement>('#accountModalTitle')!;
  const fieldsEl = root.querySelector<HTMLElement>('#accountModalFields')!;
  /*
   * Absent when `SHOW_PURPOSES` is off, which it has been since 2026-10-04 —
   * so this is a query that may find nothing, not a `!`.
   *
   * It was a `!`, and that is what broke the Edit button everywhere: hiding
   * the purposes block took the element out of the markup, `renderTags` went
   * on writing to it, and the throw happened before the modal was ever told
   * to show. The editor was populated and invisible, and the only sign was a
   * console error nobody was looking at.
   */
  const purposesEl = root.querySelector<HTMLElement>('#accountModalPurposes');
  wireDelete(root, account, token, modalEl, onDeleted, notify);
  wireIcon(root, account, token, onDeleted, notify);
  titleEl.textContent = account.name;
  fieldsEl.innerHTML = '<div id="fName" class="mb-2"></div><div id="fType" class="mb-2"></div>' +
    '<div id="fCurrencyCode" class="mb-2"></div><div id="fDescription" class="mb-2"></div>' +
    '<div id="fPlaceholder" class="mb-1"></div><div id="fMulti"></div>';

  function renderTags(): void {
    if (!purposesEl) return;
    const purposes = getPurposes();
    const tagged = new Map(getAccountPurposes().filter((ap) => ap.accountId === account.id).map((ap) => [ap.purposeId, ap.id]));
    purposesEl.innerHTML = purposes.length === 0
      ? '<p class="text-secondary small mb-0">No purposes defined yet — add one on the Currencies &amp; purposes page.</p>'
      : purposes.map((p) => {
        const linkId = tagged.get(p.id);
        return `<div class="form-check form-check-inline">
          <input class="form-check-input" type="checkbox" id="tag-${p.id}" data-purpose-id="${p.id}" ${linkId ? 'checked' : ''}>
          <label class="form-check-label" for="tag-${p.id}">${escapeHtml(p.name)}</label>
        </div>`;
      }).join('');

    purposesEl.querySelectorAll<HTMLInputElement>('[data-purpose-id]').forEach((checkbox) => {
      checkbox.addEventListener('change', () => {
        void (async () => {
          const purposeId = checkbox.dataset['purposeId']!;
          if (checkbox.checked) {
            await apiFetch('/apps/webledger/account-purposes', {
              method: 'POST', token, body: { id: crypto.randomUUID(), accountId: account.id, purposeId },
            });
          } else {
            const linkId = tagged.get(purposeId);
            if (linkId) await apiFetch(`/apps/webledger/account-purposes/${linkId}`, { method: 'DELETE', token });
          }
          await onTagsChanged();
          renderTags();
        })();
      });
    });
  }
  renderTags();

  let mounted: MountedField[] = [];
  /*
   * The modal is shown in a `finally`, and that is the point.
   *
   * Showing it used to be the last statement of this block, so anything
   * throwing on the way — a field that cannot be mounted, a lease that
   * cannot be opened — left the editor built and invisible, with the
   * rejection swallowed by the `void`. That is exactly how the Edit button
   * came to do nothing at all for three days: one element had gone, and the
   * only evidence was a console error.
   *
   * An editor missing a field is worth seeing; an editor that never appears
   * is not a failure anybody can act on. So it opens either way, and the
   * reason is said out loud rather than left in the console.
   */
  void (async () => {
    try {
      await session.openRecord('account', account.id);
      mounted.push(mountCollabField(fieldsEl.querySelector('#fName')!, { session, entity: 'account', recordId: account.id, field: 'name', label: 'Name' }));
      mounted.push(mountCollabField(fieldsEl.querySelector('#fType')!, { session, entity: 'account', recordId: account.id, field: 'type', label: 'Type' }));
      mounted.push(mountCollabField(fieldsEl.querySelector('#fCurrencyCode')!, { session, entity: 'account', recordId: account.id, field: 'currencyCode', label: 'Currency code' }));
      mounted.push(mountCollabField(fieldsEl.querySelector('#fDescription')!, { session, entity: 'account', recordId: account.id, field: 'description', label: 'Description', multiline: true }));
      mounted.push(mountCollabToggle(fieldsEl.querySelector('#fPlaceholder')!, { session, entity: 'account', recordId: account.id, field: 'isPlaceholder', label: 'Placeholder (folder)' }));
      mounted.push(mountCollabToggle(fieldsEl.querySelector('#fMulti')!, { session, entity: 'account', recordId: account.id, field: 'isMulticurrency', label: 'Multi-currency' }));
    } catch (err) {
      notify(`The editor could not be fully prepared: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      bsModal(modalEl)?.show();
    }
  })();

  const onHidden = (): void => {
    session.closeRecord('account', account.id);
    for (const m of mounted) m.destroy();
    mounted = [];
    modalEl.removeEventListener('hidden.bs.modal', onHidden);
  };
  modalEl.addEventListener('hidden.bs.modal', onHidden);
}

function wireNewAccountForm(root: HTMLElement, token: string, onCreated: () => void | Promise<void>): void {
  const form = root.querySelector<HTMLFormElement>('#newAccountForm')!;
  form.addEventListener('submit', (event) => {
    event.preventDefault();
    void (async () => {
      const data = new FormData(form);
      const name = formString(data, 'name').trim();
      const type = formString(data, 'type');
      const currencyCode = formString(data, 'currencyCode').trim().toUpperCase();
      const parentId = formString(data, 'parentId').trim();
      const isPlaceholder = data.get('isPlaceholder') === 'on';
      if (!name || !type || !currencyCode) return;

      const res = await apiFetch('/apps/webledger/accounts', {
        method: 'POST', token,
        body: { id: crypto.randomUUID(), name, type, currencyCode, isPlaceholder, ...(parentId ? { parentId } : {}) },
      });
      if (res.ok) {
        form.reset();
        bsModal(root.querySelector('#newAccountModal')!)?.hide();
        void onCreated();
      }
    })();
  });
}


/**
 * The delete button in the account editor.
 *
 * The server enforces both guards; this only asks first and reports what came
 * back. Two confirmations would be legacy's habit for its *bulk* deletes —
 * one account is recoverable (the delete is soft, invariant #7), so one is
 * enough here.
 *
 * Re-bound on every open rather than once: the modal element outlives any one
 * account, so a listener left attached would delete whichever account the
 * closure it came from was about.
 */
function wireDelete(
  root: HTMLElement, account: AccountDto, token: string, modalEl: HTMLElement,
  onDeleted: () => Promise<void>, notify: (m: string) => void,
): void {
  const button = root.querySelector<HTMLButtonElement>('#accountDelete');
  const note = root.querySelector<HTMLElement>('#accountDeleteNote');
  if (!button || !note) return;
  note.textContent = '';

  const fresh = button.cloneNode(true) as HTMLButtonElement;
  button.replaceWith(fresh);
  fresh.addEventListener('click', () => {
    void (async () => {
      const go = await askConfirm({
        title: `Delete the account "${account.name}"?`,
        message: 'The deletion is soft: it stays under "Deleted records", where anyone who can '
          + 'delete it can put it back. An account with transactions or sub-accounts is '
          + 'refused rather than emptied.',
        confirm: 'Delete the account', cancel: 'Keep it', tone: 'danger',
      });
      if (!go) return;
      fresh.disabled = true;
      const res = await apiFetch(`/apps/webledger/accounts/${account.id}`, { method: 'DELETE', token })
        .catch(() => undefined);
      fresh.disabled = false;
      if (res?.ok) {
        bsModal(modalEl)?.hide();
        notify(`Deleted "${account.name}".`);
        await onDeleted();
        return;
      }
      // The two guards are the interesting answers, and each names what to do
      // about it: move the transactions, or delete the sub-accounts first.
      note.textContent = await failureMessage(res, 'Could not delete this account.');
    })();
  });
}


/**
 * Drag an account above or below one of its siblings — legacy's `wireDrag`.
 *
 * A drop is valid only when the target shares the dragged row's **parent and
 * type** and is not the row itself. That is narrower than it looks and it is
 * the point: this gesture reorders, it never *moves*. Allowing a drag to
 * cross parents would reparent an account — a change to the shape of the
 * chart of accounts — through a gesture that looks like tidying, and allowing
 * it to cross types would put an expense under assets. Both are real edits and
 * belong in the editor, where they are deliberate and reversible.
 */
function wireAccountDrag(
  container: HTMLElement, token: string, onReordered: () => Promise<void>, fail: (m: string) => void,
): void {
  let dragging: { id: string; parent: string; type: string } | undefined;

  for (const tr of container.querySelectorAll<HTMLTableRowElement>('tr[data-row][draggable="true"]')) {
    const info = (row: HTMLElement): { id: string; parent: string; type: string } => ({
      id: row.dataset['row']!, parent: row.dataset['parent'] ?? '', type: row.dataset['type'] ?? '',
    });
    const compatible = (row: HTMLElement): boolean =>
      dragging !== undefined && row.dataset['row'] !== dragging.id
      && (row.dataset['parent'] ?? '') === dragging.parent && (row.dataset['type'] ?? '') === dragging.type;

    tr.addEventListener('dragstart', (event) => {
      dragging = info(tr);
      tr.classList.add('wl-dragging');
      // Some browsers refuse a drag with no payload, so set one even though
      // the ids are tracked in this closure.
      try { event.dataTransfer?.setData('text/plain', dragging.id); } catch { /* refused */ }
      if (event.dataTransfer) event.dataTransfer.effectAllowed = 'move';
    });
    tr.addEventListener('dragover', (event) => {
      if (!compatible(tr)) return;
      event.preventDefault();
      tr.classList.add('wl-drop-target');
    });
    tr.addEventListener('dragleave', () => tr.classList.remove('wl-drop-target'));
    tr.addEventListener('drop', (event) => {
      tr.classList.remove('wl-drop-target');
      if (!compatible(tr)) return;
      event.preventDefault();
      const moved = dragging!;
      dragging = undefined;
      void applyAccountReorder(container, moved, tr.dataset['row']!, token, onReordered, fail);
    });
    tr.addEventListener('dragend', () => {
      dragging = undefined;
      container.querySelectorAll('.wl-drop-target').forEach((el) => el.classList.remove('wl-drop-target'));
      container.querySelectorAll('.wl-dragging').forEach((el) => el.classList.remove('wl-dragging'));
    });
  }
}

async function applyAccountReorder(
  container: HTMLElement, moved: { id: string; parent: string }, targetId: string,
  token: string, onReordered: () => Promise<void>, fail: (m: string) => void,
): Promise<void> {
  // The siblings as currently displayed, which is the order the server will
  // be told to write — so what the user saw is what gets saved.
  const siblings = [...container.querySelectorAll<HTMLElement>('tr[data-row]')]
    .filter((row) => (row.dataset['parent'] ?? '') === moved.parent)
    .map((row) => row.dataset['row']!);
  const from = siblings.indexOf(moved.id);
  const to = siblings.indexOf(targetId);
  if (from < 0 || to < 0 || from === to) return;
  siblings.splice(to, 0, ...siblings.splice(from, 1));

  const res = await apiFetch('/apps/webledger/accounts/reorder', {
    method: 'POST', token, body: { parentId: moved.parent || null, orderedIds: siblings },
  }).catch(() => undefined);
  // Nothing on screen moves until `onReordered` re-renders, so a failure here
  // left the tree exactly as it was — indistinguishable from a drop that
  // missed. `fail` says why, on screen as well as to a screen reader.
  if (!res?.ok) { fail('Could not save the new order.'); return; }
  await onReordered();
}


/** What a raster icon is downscaled to before upload. A 64px icon needs no more. */
const ICON_MAX_PX = 128;
const ICON_MAX_BYTES = 48 * 1024;

/**
 * Choose or remove an account's icon — legacy's `icon_upload`/`icon_delete`.
 *
 * A raster file is **downscaled in the browser** to at most 128px and
 * re-encoded as PNG before it is sent. That is where legacy's thumbnailing
 * went: doing it here keeps an image library out of `api` (which CLAUDE.md
 * says needs a reason), and it means someone can pick a 4 MB photo and get a
 * sensible icon instead of an error telling them to go and resize it.
 *
 * SVG is passed through untouched — rasterising a vector icon would throw
 * away the thing that makes it one — and simply refused if it is too big,
 * which for an icon means it is not really an icon.
 */
function wireIcon(
  root: HTMLElement, account: AccountDto, token: string,
  onChanged: () => Promise<void>, notify: (m: string) => void,
): void {
  const preview = root.querySelector<HTMLElement>('#accountIconPreview');
  const input = root.querySelector<HTMLInputElement>('#accountIconFile');
  const clear = root.querySelector<HTMLButtonElement>('#accountIconClear');
  const note = root.querySelector<HTMLElement>('#accountIconNote');
  if (!preview || !input || !clear || !note) return;

  note.textContent = '';
  preview.innerHTML = account.iconPath
    ? `<img class="wl-acct-icon" src="${escapeHtml(iconUrl(account.iconPath))}" alt="">`
    : accountIconHtml(account.type, account.isPlaceholder);

  const freshInput = input.cloneNode(true) as HTMLInputElement;
  input.replaceWith(freshInput);
  const freshClear = clear.cloneNode(true) as HTMLButtonElement;
  clear.replaceWith(freshClear);

  freshInput.addEventListener('change', () => {
    void (async () => {
      const file = freshInput.files?.[0];
      if (!file) return;
      note.textContent = 'Preparing…';
      const prepared = await prepareIcon(file).catch(() => undefined);
      if (!prepared) { note.textContent = 'That file could not be read as an image.'; return; }
      if (prepared.bytes > ICON_MAX_BYTES) {
        note.textContent = `That icon is ${Math.round(prepared.bytes / 1024)} KB; the limit is ${ICON_MAX_BYTES / 1024} KB.`;
        return;
      }
      const res = await apiFetch(`/apps/webledger/accounts/${account.id}/icon`, {
        method: 'PUT', token, body: { mime: prepared.mime, dataBase64: prepared.dataBase64 },
      }).catch(() => undefined);
      if (!res?.ok) {
        note.textContent = await failureMessage(res, 'Could not save that icon.');
        return;
      }
      note.textContent = '';
      notify('Icon updated.');
      await onChanged();
    })();
  });

  freshClear.addEventListener('click', () => {
    void (async () => {
      const res = await apiFetch(`/apps/webledger/accounts/${account.id}/icon`, { method: 'DELETE', token })
        .catch(() => undefined);
      if (!res?.ok) { note.textContent = 'Could not remove that icon.'; return; }
      preview.innerHTML = accountIconHtml(account.type, account.isPlaceholder);
      notify('Icon removed.');
      await onChanged();
    })();
  });
}

async function prepareIcon(file: File): Promise<{ mime: string; dataBase64: string; bytes: number }> {
  const buffer = await file.arrayBuffer();
  const toBase64 = (bytes: ArrayBuffer): string => {
    let binary = '';
    for (const byte of new Uint8Array(bytes)) binary += String.fromCharCode(byte);
    return btoa(binary);
  };

  if (file.type === 'image/svg+xml' || file.name.toLowerCase().endsWith('.svg')) {
    return { mime: 'image/svg+xml', dataBase64: toBase64(buffer), bytes: buffer.byteLength };
  }

  const bitmap = await createImageBitmap(new Blob([buffer]));
  const scale = Math.min(1, ICON_MAX_PX / Math.max(bitmap.width, bitmap.height));
  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, Math.round(bitmap.width * scale));
  canvas.height = Math.max(1, Math.round(bitmap.height * scale));
  canvas.getContext('2d')!.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
  const dataUrl = canvas.toDataURL('image/png');
  const dataBase64 = dataUrl.slice(dataUrl.indexOf(',') + 1);
  return { mime: 'image/png', dataBase64, bytes: Math.floor(dataBase64.length * 0.75) };
}


function mergeAccountsModalHtml(): string {
  return `
    <div class="modal fade" id="mergeAccountsModal" tabindex="-1" aria-hidden="true">
      <div class="modal-dialog modal-lg">
        <div class="modal-content">
          <div class="modal-header py-2">
            <div class="fw-semibold">Merge accounts</div>
            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
          </div>
          <form id="mergeAccountsForm">
            <div class="modal-body">
              <p class="small text-secondary">
                Collapses several single-currency accounts into one new multi-currency account — for when
                the same thing was kept separately per currency. Every transaction moves to the new account
                keeping its own currency; the originals are then deleted. A <strong>new</strong> account is
                always created, so this never merges into an existing one.
              </p>
              <div class="row g-2">
                <div class="col-md-5"><label class="form-label">New account name</label>
                  <input class="form-control form-control-sm" name="name" required></div>
                <div class="col-md-3"><label class="form-label">Type</label>
                  <select class="form-select form-select-sm" name="type" required>
                    <option value="ASSET">Asset</option><option value="LIABILITY">Liability</option>
                    <option value="EQUITY">Equity</option><option value="INCOME">Income</option>
                    <option value="EXPENSE">Expense</option>
                  </select></div>
                <div class="col-md-4"><label class="form-label">Default currency</label>
                  <input class="form-control form-control-sm" name="currencyCode" maxlength="3" placeholder="EUR" required></div>
              </div>
              <div class="mt-2"><label class="form-label">Parent account</label>
                <select class="form-select form-select-sm" name="parentId" id="mergeParent"><option value="">(top level)</option></select></div>
              <div class="mt-3"><div class="cf-h mb-1">Accounts to merge</div>
                <div id="mergeSources" class="wl-merge-sources"></div></div>
            </div>
            <div class="modal-footer py-2">
              <span class="small text-secondary me-auto" id="mergeNote"></span>
              <button class="btn btn-sm btn-danger" type="submit">Merge</button>
            </div>
          </form>
        </div>
      </div>
    </div>`;
}

function wireMergeForm(
  root: HTMLElement, token: string, getAccounts: () => AccountDto[],
  onMerged: () => Promise<void>, notify: (m: string) => void,
): void {
  const modalEl = root.querySelector<HTMLElement>('#mergeAccountsModal')!;
  const form = root.querySelector<HTMLFormElement>('#mergeAccountsForm')!;
  const sourcesEl = root.querySelector<HTMLElement>('#mergeSources')!;
  const parentEl = root.querySelector<HTMLSelectElement>('#mergeParent')!;
  const note = root.querySelector<HTMLElement>('#mergeNote')!;

  // Built on open rather than once: the account list changes underneath, and
  // a stale checkbox list is how you merge the wrong thing.
  modalEl.addEventListener('show.bs.modal', () => {
    const accounts = getAccounts();
    note.textContent = '';
    // Only postable leaves can be merged, so folders and parents are left out
    // rather than offered and then refused.
    const parents = new Set(accounts.map((a) => a.parentId).filter(Boolean));
    const eligible = accounts.filter((a) => !a.isPlaceholder && !parents.has(a.id));
    sourcesEl.innerHTML = eligible.map((a) => `<label class="form-check">
      <input class="form-check-input" type="checkbox" name="sourceIds" value="${escapeHtml(a.id)}">
      <span class="form-check-label">${escapeHtml(accountPath(accounts, a.id))}
        <span class="text-secondary small">${escapeHtml(a.type)} · ${escapeHtml(a.currencyCode)}</span></span>
    </label>`).join('');
    parentEl.innerHTML = '<option value="">(top level)</option>'
      + visibleRows(buildAccountTree(accounts), new Set())
        .map((n) => `<option value="${n.account.id}">${'\u00a0\u00a0'.repeat(n.depth)}${escapeHtml(n.account.name)}</option>`)
        .join('');
  });

  form.addEventListener('submit', (event) => {
    event.preventDefault();
    void (async () => {
      const data = new FormData(form);
      const sourceIds = [...sourcesEl.querySelectorAll<HTMLInputElement>('input:checked')].map((i) => i.value);
      note.textContent = 'Merging…';
      const res = await apiFetch('/apps/webledger/accounts/merge', {
        method: 'POST', token,
        body: {
          id: crypto.randomUUID(),
          name: formString(data, 'name'),
          type: formString(data, 'type'),
          currencyCode: formString(data, 'currencyCode'),
          parentId: formString(data, 'parentId') || undefined,
          sourceIds,
        },
      }).catch(() => undefined);
      if (!res?.ok) { note.textContent = await failureMessage(res, 'Could not merge those accounts.'); return; }
      const body = (await res.json().catch(() => ({}))) as { movedSplits?: number; merged?: number };
      note.textContent = '';
      bsModal(modalEl)?.hide();
      form.reset();
      notify(`Merged ${body.merged ?? 0} accounts, moving ${body.movedSplits ?? 0} transaction legs.`);
      await onMerged();
    })();
  });
}
